import { useState } from "react";
import { X, ChevronDown, ChevronUp, Plus, Trash2, Loader2, Globe, Info, Search, FileText, ExternalLink, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "react-hot-toast";
import webScraperService, {
  WebScrapeJob,
  WebScraperFileType,
  PreviewUrlResult,
  WebScraperLoginConfig,
} from "../../../services/web-scraper.service";

interface CreateJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentStoreId: string;
  storeName: string;
  onCreated: (job: WebScrapeJob) => void;
}

const FILE_TYPE_OPTIONS: WebScraperFileType[] = [
  WebScraperFileType.PDF,
  WebScraperFileType.DOCX,
  WebScraperFileType.XLSX,
  WebScraperFileType.PPTX,
  WebScraperFileType.CSV,
  WebScraperFileType.IMAGE,
  WebScraperFileType.ZIP,
];

interface CustomHeader {
  key: string;
  value: string;
}

// Reads a numeric input that may be blank; returns undefined for blank/NaN so the
// payload omits the field entirely (= unlimited on the backend).
function parseOptionalNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

export default function CreateJobModal({
  isOpen,
  onClose,
  documentStoreId,
  storeName,
  onCreated,
}: CreateJobModalProps) {
  const [seedUrl, setSeedUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Test URL preview state
  const [isTesting, setIsTesting] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewUrlResult | null>(null);
  const [previewError, setPreviewError] = useState<string>("");

  // Advanced fields — all blank by default. Empty input == "no limit".
  // Stored as strings so the user can leave them empty without React fighting
  // them with NaN. parseOptionalNumber() converts at submit time.
  const [maxDepth, setMaxDepth] = useState<string>("");
  const [maxPages, setMaxPages] = useState<string>("");
  const [maxFileSizeMb, setMaxFileSizeMb] = useState<string>("");
  const [concurrency, setConcurrency] = useState<string>("");
  const [requestDelayMs, setRequestDelayMs] = useState<string>("");
  const [allowedFileTypes, setAllowedFileTypes] = useState<WebScraperFileType[]>([]);
  const [respectRobots, setRespectRobots] = useState(true);
  const [usePlaywright, setUsePlaywright] = useState(false);
  const [customHeaders, setCustomHeaders] = useState<CustomHeader[]>([]);
  const [localStorageEntries, setLocalStorageEntries] = useState<CustomHeader[]>([]);

  // Login config
  const [useLoginConfig, setUseLoginConfig] = useState(false);
  const [loginUrl, setLoginUrl] = useState("");
  const [loginUsernameSelector, setLoginUsernameSelector] = useState("");
  const [loginPasswordSelector, setLoginPasswordSelector] = useState("");
  const [loginSubmitSelector, setLoginSubmitSelector] = useState("");
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginSuccessUrlPattern, setLoginSuccessUrlPattern] = useState("");
  const [loginWaitForSelector, setLoginWaitForSelector] = useState("");

  if (!isOpen) return null;

  const validateUrl = (url: string) => {
    if (!url.trim()) return "Seed URL is required";
    try {
      const parsed = new URL(url);
      if (!["http:", "https:"].includes(parsed.protocol)) return "URL must start with http:// or https://";
    } catch {
      return "Please enter a valid URL";
    }
    return "";
  };

  const buildLoginConfig = (): WebScraperLoginConfig | undefined => {
    if (!useLoginConfig || !loginUrl.trim()) return undefined;
    return {
      login_url: loginUrl.trim(),
      username_selector: loginUsernameSelector.trim(),
      password_selector: loginPasswordSelector.trim(),
      submit_selector: loginSubmitSelector.trim(),
      username: loginUsername,
      password: loginPassword,
      ...(loginSuccessUrlPattern.trim() ? { success_url_pattern: loginSuccessUrlPattern.trim() } : {}),
      ...(loginWaitForSelector.trim() ? { wait_for_selector: loginWaitForSelector.trim() } : {}),
    };
  };

  const handleTestUrl = async () => {
    const err = validateUrl(seedUrl);
    if (err) { setUrlError(err); return; }
    setUrlError("");
    setPreviewError("");
    setPreviewResult(null);
    setIsTesting(true);
    try {
      const headersObj: Record<string, string> = {};
      customHeaders.forEach(({ key, value }) => { if (key.trim() && value.trim()) headersObj[key.trim()] = value.trim(); });
      const localStorageObj: Record<string, string> = {};
      localStorageEntries.forEach(({ key, value }) => { if (key.trim() && value.trim()) localStorageObj[key.trim()] = value.trim(); });

      const result = await webScraperService.previewUrl({
        url: seedUrl.trim(),
        use_playwright_fallback: usePlaywright,
        custom_headers: Object.keys(headersObj).length > 0 ? headersObj : undefined,
        local_storage: Object.keys(localStorageObj).length > 0 ? localStorageObj : undefined,
        login_config: buildLoginConfig(),
      });
      setPreviewResult(result);
    } catch (e: any) {
      const msg = e?.response?.data?.error?.error_description || "Could not fetch the URL";
      setPreviewError(msg);
    } finally {
      setIsTesting(false);
    }
  };

  const toggleFileType = (ft: WebScraperFileType) => {
    setAllowedFileTypes((prev) =>
      prev.includes(ft) ? prev.filter((t) => t !== ft) : [...prev, ft]
    );
  };

  const addHeader = () => setCustomHeaders((prev) => [...prev, { key: "", value: "" }]);
  const removeHeader = (i: number) => setCustomHeaders((prev) => prev.filter((_, idx) => idx !== i));
  const updateHeader = (i: number, field: "key" | "value", val: string) => {
    setCustomHeaders((prev) => prev.map((h, idx) => (idx === i ? { ...h, [field]: val } : h)));
  };

  const addLocalStorage = () => setLocalStorageEntries((prev) => [...prev, { key: "", value: "" }]);
  const removeLocalStorage = (i: number) => setLocalStorageEntries((prev) => prev.filter((_, idx) => idx !== i));
  const updateLocalStorage = (i: number, field: "key" | "value", val: string) => {
    setLocalStorageEntries((prev) => prev.map((h, idx) => (idx === i ? { ...h, [field]: val } : h)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateUrl(seedUrl);
    if (err) { setUrlError(err); return; }
    setUrlError("");
    setIsSubmitting(true);
    try {
      const headersObj: Record<string, string> = {};
      customHeaders.forEach(({ key, value }) => { if (key.trim() && value.trim()) headersObj[key.trim()] = value.trim(); });

      const localStorageObj: Record<string, string> = {};
      localStorageEntries.forEach(({ key, value }) => { if (key.trim() && value.trim()) localStorageObj[key.trim()] = value.trim(); });

      if (localStorageEntries.length > 10) {
        toast.error("More than 10 local storage entries is unusual — please review");
      }

      const maxFileSizeMbNum = parseOptionalNumber(maxFileSizeMb);

      const job = await webScraperService.createJob({
        document_store_id: documentStoreId,
        seed_url: seedUrl.trim(),
        max_depth: parseOptionalNumber(maxDepth),
        max_pages: parseOptionalNumber(maxPages),
        max_file_size_bytes: maxFileSizeMbNum != null ? maxFileSizeMbNum * 1_048_576 : undefined,
        concurrency: parseOptionalNumber(concurrency),
        request_delay_ms: parseOptionalNumber(requestDelayMs),
        allowed_file_types: allowedFileTypes.length > 0 ? allowedFileTypes : undefined,
        use_playwright_fallback: usePlaywright,
        respect_robots_txt: respectRobots,
        crawl_subdomains: false,
        custom_headers: Object.keys(headersObj).length > 0 ? headersObj : undefined,
        local_storage: Object.keys(localStorageObj).length > 0 ? localStorageObj : undefined,
        login_config: buildLoginConfig(),
      });
      toast.success("Scrape job created");
      onCreated(job);
    } catch {
      toast.error("Failed to create scrape job");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">New Scrape Job</h2>
              <p className="text-xs text-gray-500">Importing into: {storeName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors" type="button">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* What this does */}
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
            <p className="text-sm text-purple-900 leading-relaxed">
              Web Scraper visits a website, follows the links it finds, and pulls every document it discovers into your DMS.
              Paste the URL where you want it to start and click <strong>Create Job</strong>. Advanced settings are optional — leave them blank for no limits.
            </p>
          </div>

          {/* Seed URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website URL <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={seedUrl}
                onChange={(e) => { setSeedUrl(e.target.value); setUrlError(""); setPreviewResult(null); setPreviewError(""); }}
                placeholder="https://example.com/reports"
                className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${urlError ? "border-red-400" : "border-gray-300"}`}
              />
              <button
                type="button"
                onClick={handleTestUrl}
                disabled={isTesting || !seedUrl.trim()}
                title="Fetch this URL once and show what a real crawl would find — without creating a job"
                className="flex items-center gap-1.5 px-3 py-2 border border-purple-300 text-purple-700 bg-purple-50 rounded-lg text-sm font-medium hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                {isTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Test URL
              </button>
            </div>
            {urlError
              ? <p className="text-xs text-red-500 mt-1">{urlError}</p>
              : <p className="text-xs text-gray-500 mt-1">The page where the crawl starts. Only links on this exact domain will be followed.</p>
            }
          </div>

          {/* Preview result */}
          {previewError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold text-red-900">Couldn't fetch this URL</p>
                <p className="text-red-700 mt-0.5">{previewError}</p>
                {usePlaywright
                  ? <p className="text-red-700 mt-1">If the site requires auth, double-check your custom headers and local-storage entries below.</p>
                  : <p className="text-red-700 mt-1">If the site needs a browser to render (React / Vue / Angular), turn on <strong>JavaScript Rendering</strong> in Advanced Settings.</p>
                }
              </div>
            </div>
          )}

          {previewResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-green-900">
                    Fetched in {previewResult.fetch_time_ms}ms
                  </p>
                  {previewResult.page_title && (
                    <p className="text-xs text-gray-700 mt-0.5 truncate" title={previewResult.page_title}>
                      Page title: <span className="font-medium">{previewResult.page_title}</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">Documents</p>
                  <p className="text-lg font-bold text-gray-900">{previewResult.file_link_count}</p>
                </div>
                <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">Internal pages</p>
                  <p className="text-lg font-bold text-gray-900">{previewResult.page_link_count}</p>
                </div>
                <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide">External links</p>
                  <p className="text-lg font-bold text-gray-900">{previewResult.external_link_count}</p>
                </div>
              </div>

              {previewResult.file_links.length > 0 && (
                <div>
                  <p className="text-[11px] font-medium text-gray-600 mb-1.5">Sample documents found</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto chat-scrollbar">
                    {previewResult.file_links.slice(0, 8).map((f) => (
                      <a
                        key={f.url}
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs text-gray-700 hover:text-purple-700 bg-white px-2 py-1 rounded border border-green-100"
                      >
                        <FileText className="w-3 h-3 text-purple-600 flex-shrink-0" />
                        <span className="font-medium flex-shrink-0">{f.fileType}</span>
                        <span className="truncate" title={f.url}>{f.fileName}</span>
                        <ExternalLink className="w-2.5 h-2.5 text-gray-400 flex-shrink-0 ml-auto" />
                      </a>
                    ))}
                    {previewResult.file_links.length > 8 && (
                      <p className="text-[11px] text-gray-500 italic px-2">…and {previewResult.file_links.length - 8} more</p>
                    )}
                  </div>
                </div>
              )}

              {previewResult.file_link_count === 0 && previewResult.page_link_count === 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  No internal links or documents found on this page. The crawl will likely import just this single page.
                </p>
              )}

              {previewResult.capped && (
                <p className="text-[11px] text-gray-500 italic">Showing the first 50 of many — a real crawl will follow all of them.</p>
              )}
            </div>
          )}

          {/* Advanced Settings toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Advanced Settings
            <span className="text-xs text-gray-400 font-normal">(optional — leave blank for no limits)</span>
          </button>

          {showAdvanced && (
            <div className="space-y-4 border border-gray-200 rounded-lg p-4">
              {/* Row: max depth + max pages */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max Depth</label>
                  <input
                    type="number"
                    value={maxDepth}
                    onChange={(e) => setMaxDepth(e.target.value)}
                    placeholder="No limit"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">How many clicks away from the start URL to follow. Blank = follow links as deep as they go.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max Pages</label>
                  <input
                    type="number"
                    value={maxPages}
                    onChange={(e) => setMaxPages(e.target.value)}
                    placeholder="No limit"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Total pages the crawler will visit. Blank = until everything reachable is visited.</p>
                </div>
              </div>

              {/* Row: max file size + concurrency */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max File Size (MB)</label>
                  <input
                    type="number"
                    value={maxFileSizeMb}
                    onChange={(e) => setMaxFileSizeMb(e.target.value)}
                    placeholder="No limit"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Files larger than this are skipped. Blank = any size accepted.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Concurrency</label>
                  <input
                    type="number"
                    value={concurrency}
                    onChange={(e) => setConcurrency(e.target.value)}
                    placeholder="Auto"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">How many pages to fetch in parallel. Blank = engine default.</p>
                </div>
              </div>

              {/* Request delay */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Request Delay (ms)</label>
                <input
                  type="number"
                  value={requestDelayMs}
                  onChange={(e) => setRequestDelayMs(e.target.value)}
                  placeholder="No delay"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-[11px] text-gray-500 mt-1">Pause between requests, in milliseconds. Blank = fire as fast as possible.</p>
              </div>

              {/* File types */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">File Types to Import</label>
                <div className="flex flex-wrap gap-2">
                  {FILE_TYPE_OPTIONS.map((ft) => (
                    <button
                      key={ft}
                      type="button"
                      onClick={() => toggleFileType(ft)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        allowedFileTypes.includes(ft)
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-gray-600 border-gray-300 hover:border-purple-400"
                      }`}
                    >
                      {ft}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Leave empty to import every supported file type. Anything filtered out shows up under Skipped URLs.</p>
              </div>

              {/* Toggles */}
              <div className="space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <div
                    onClick={() => setRespectRobots((v) => !v)}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 mt-0.5 ${respectRobots ? "bg-purple-600" : "bg-gray-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${respectRobots ? "translate-x-5" : ""}`} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-700">Respect robots.txt</span>
                    <p className="text-[11px] text-gray-500">Honour the site's crawl rules. Recommended — keep this on unless you own the site.</p>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <div
                    onClick={() => setUsePlaywright((v) => !v)}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 mt-0.5 ${usePlaywright ? "bg-purple-600" : "bg-gray-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${usePlaywright ? "translate-x-5" : ""}`} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-700">JavaScript Rendering</span>
                    <p className="text-[11px] text-gray-500">Use a real browser. Turn on for React / Vue / Angular sites where content only appears after JS runs.</p>
                  </div>
                </label>
              </div>

              {/* Playwright-only fields: Custom Headers + Local Storage */}
              {usePlaywright && (
                <div className="space-y-4 border-t border-dashed border-gray-200 pt-4">
                  <p className="text-xs text-blue-600 font-medium">
                    Custom Headers and Local Storage require JavaScript Rendering.
                  </p>

                  {/* Custom Headers */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600">Custom Headers</label>
                      <button type="button" onClick={addHeader}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700">
                        <Plus className="w-3 h-3" /> Add Header
                      </button>
                    </div>
                    {customHeaders.map((h, i) => (
                      <div key={i} className="flex gap-2 mb-2">
                        <input
                          placeholder="Header name"
                          value={h.key}
                          onChange={(e) => updateHeader(i, "key", e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <input
                          placeholder="Value"
                          value={h.value}
                          onChange={(e) => updateHeader(i, "value", e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <button type="button" onClick={() => removeHeader(i)}
                          className="p-1.5 text-red-400 hover:text-red-600 rounded">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {customHeaders.length === 0 && (
                      <p className="text-xs text-gray-400 italic">No custom headers — click "Add Header" to add one</p>
                    )}
                  </div>

                  {/* Local Storage */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs font-medium text-gray-600">Local Storage Injection</label>
                        <div className="group relative">
                          <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                          <div className="absolute left-0 bottom-5 z-10 hidden group-hover:block w-72 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl leading-relaxed">
                            Open the target site while logged in → F12 → Application → Local Storage → copy the keys and values your app stores the auth token under.
                          </div>
                        </div>
                      </div>
                      <button type="button" onClick={addLocalStorage}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700">
                        <Plus className="w-3 h-3" /> Add Entry
                      </button>
                    </div>
                    {localStorageEntries.length > 10 && (
                      <p className="text-xs text-amber-600 mb-2">More than 10 entries is unusual — please review.</p>
                    )}
                    {localStorageEntries.map((entry, i) => (
                      <div key={i} className="flex gap-2 mb-2">
                        <input
                          placeholder="Key (e.g. access_token)"
                          value={entry.key}
                          onChange={(e) => updateLocalStorage(i, "key", e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <input
                          placeholder='Value (e.g. eyJhbGci… or {"id":"1"})'
                          value={entry.value}
                          onChange={(e) => updateLocalStorage(i, "value", e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <button type="button" onClick={() => removeLocalStorage(i)}
                          className="p-1.5 text-red-400 hover:text-red-600 rounded">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {localStorageEntries.length === 0 && (
                      <p className="text-xs text-gray-400 italic">No entries — click "Add Entry" to inject localStorage values</p>
                    )}
                  </div>
                </div>
              )}

              {/* Form-based Login — works with or without JavaScript Rendering */}
              <div className="border-t border-dashed border-gray-200 pt-4">
                <label className="flex items-start gap-2 cursor-pointer mb-3">
                  <div
                    onClick={() => setUseLoginConfig((v) => !v)}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 mt-0.5 ${useLoginConfig ? "bg-purple-600" : "bg-gray-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${useLoginConfig ? "translate-x-5" : ""}`} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-700">Form-based Login</span>
                    <p className="text-[11px] text-gray-500">
                      {usePlaywright
                        ? "Puppeteer logs in and crawls all pages authenticated."
                        : "Puppeteer logs in to get session cookies, then Cheerio crawls all pages (faster)."}
                    </p>
                  </div>
                </label>

                {useLoginConfig && (
                  <div className="space-y-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Login Page URL <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={loginUrl}
                        onChange={(e) => setLoginUrl(e.target.value)}
                        placeholder="https://example.com/login"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Username Field Selector <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={loginUsernameSelector}
                          onChange={(e) => setLoginUsernameSelector(e.target.value)}
                          placeholder="#email or input[name=username]"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Password Field Selector <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={loginPasswordSelector}
                          onChange={(e) => setLoginPasswordSelector(e.target.value)}
                          placeholder="#password or input[type=password]"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Submit Button Selector <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={loginSubmitSelector}
                        onChange={(e) => setLoginSubmitSelector(e.target.value)}
                        placeholder="button[type=submit] or #login-btn"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Username <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={loginUsername}
                          onChange={(e) => setLoginUsername(e.target.value)}
                          placeholder="user@example.com"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Password <span className="text-red-500">*</span></label>
                        <input
                          type="password"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder="••••••••"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Success URL Pattern
                          <span className="text-gray-400 font-normal ml-1">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={loginSuccessUrlPattern}
                          onChange={(e) => setLoginSuccessUrlPattern(e.target.value)}
                          placeholder="/dashboard"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <p className="text-[11px] text-gray-400 mt-0.5">URL substring to confirm login succeeded</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Wait For Selector
                          <span className="text-gray-400 font-normal ml-1">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={loginWaitForSelector}
                          onChange={(e) => setLoginWaitForSelector(e.target.value)}
                          placeholder=".user-avatar or #main-nav"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <p className="text-[11px] text-gray-400 mt-0.5">Wait for this element after redirect</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <footer className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
            Create Job
          </button>
        </footer>
      </div>
    </div>
  );
}
